import React from "react";
import styled, { keyframes } from "styled-components";
import {
  BREAKPOINT_RESERVATION_PAGE,
  LeftColumn,
  OlympiacosReservationScopedGlobalStyle,
  PageLayout,
  ReservationPageScopedGlobalStyle,
  RightColumn,
} from "../../pageComponents/reservation-shared-styles";
import useMiniSite from "../../hooks/useMiniSite";

const { isOlympiacos } = useMiniSite();

const pulse = keyframes`
            0%   { opacity: 1; }
            50%  { opacity: 0.4; }
            100% { opacity: 1; }
          `;

const SkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SkeletonBox = styled.div`
  background-color: #e9e8ec;
  border-radius: 4px;
  animation: ${pulse} 1.5s infinite ease-in-out;
`;

const TopBar = styled(SkeletonBox)`
  max-width: 184px;
  width: 100%;
  height: 16px;
`;

const SkeletonLine = styled(SkeletonBox)<{
  width?: string;
  height?: string;
  $mb?: number;
  $mt?: number;
}>`
  width: ${({ width }) => width || "100%"};
  height: ${({ height }) => height || "16px"};
  margin-bottom: ${({ $mb }) => ($mb ? `${$mb}px` : "0")};
  margin-top: ${({ $mt }) => ($mt ? `${$mt}px` : "0")};

  box-sizing: border-box;
`;

const HideOnMobile = styled.div`
  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    display: none;
  }
`;

const FullWidthOnMobile = styled.div`
  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    & > * {
      width: 100%;
      margin-top: 8px;
    }
  }
`;

const ShowOnMobile = styled.div`
  display: none;
  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    display: contents;
  }
`;

const FlexContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FlexWithGap = styled.div`
  display: flex;
  gap: 12px;
`;

const RightCard = styled.div`
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  align-items: stretch;
  min-width: 320px;
  background: #fff;
`;

const CardImage = styled(SkeletonBox)`
  width: 92px;
  height: 92px;

  flex: 0 0 92px;
`;

const CardLines = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTop = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-start;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: column;
`;

const BottomContainer = styled.div`
  display: none;
  justify-content: space-between;
  border-top: 1px solid var(--Colors-grey-grey-600, #d8dbe0);
  padding: 16px;
  background: #fff;

  @media (max-width: ${BREAKPOINT_RESERVATION_PAGE}) {
    display: flex;
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    z-index: 999;
    padding-bottom: calc(16px + env(safe-area-inset-bottom));
    box-sizing: border-box;
    align-items: center;
  }
`;

const SplitOrderSkeleton = () => (
  <>
    <ReservationPageScopedGlobalStyle />
    {isOlympiacos && <OlympiacosReservationScopedGlobalStyle />}
    <SkeletonWrapper>
      <PageLayout>
        <LeftColumn className="left">
          <SkeletonLine width="140px" height="24px" />

          <HideOnMobile>
            <SkeletonLine width="50%" height="16px" $mt={8} $mb={8} />
            <SkeletonLine width="50%" height="16px" $mb={24} />

            <SkeletonLine width="140px" height="24px" />
            <SkeletonLine width="50%" height="16px" $mt={8} $mb={8} />
            <FlexColumn>
              <FlexWithGap>
                <SkeletonLine width="20px" height="20px" $mb={12} />
                <SkeletonLine width="94%" height="20px" $mb={12} />
              </FlexWithGap>
              <FlexWithGap>
                <SkeletonLine width="20px" height="20px" $mb={12} />
                <SkeletonLine width="94%" height="20px" $mb={12} />
              </FlexWithGap>
              <FlexWithGap>
                <SkeletonLine width="20px" height="20px" $mb={12} />
                <SkeletonLine width="94%" height="20px" $mb={12} />
              </FlexWithGap>
              <FlexWithGap>
                <SkeletonLine width="20px" height="20px" $mb={12} />
                <SkeletonLine width="94%" height="20px" $mb={12} />
              </FlexWithGap>
              <FlexWithGap>
                <SkeletonLine width="20px" height="20px" $mb={12} />
                <SkeletonLine width="94%" height="20px" $mb={12} />
              </FlexWithGap>
              <FlexWithGap>
                <SkeletonLine width="20px" height="20px" $mb={12} />
                <SkeletonLine width="94%" height="20px" $mb={12} />
              </FlexWithGap>
              <FlexWithGap>
                <SkeletonLine width="20px" height="20px" $mb={12} />
                <SkeletonLine width="94%" height="20px" $mb={24} />
              </FlexWithGap>
            </FlexColumn>
          </HideOnMobile>

          <FullWidthOnMobile>
            <SkeletonLine width="60%" height="16px" $mb={8} />
            <SkeletonLine width="60%" height="16px" />
          </FullWidthOnMobile>
        </LeftColumn>

        <RightColumn className="right">
          <RightCard>
            <CardTop>
              <CardImage />
              <CardLines>
                <SkeletonLine height="24px" $mb={28} />
                <SkeletonLine height="16px" $mb={8} />
                <SkeletonLine height="16px" />
              </CardLines>
            </CardTop>

            <FlexColumn>
              <SkeletonLine width="40%" height="20px" $mb={16} />

              <HideOnMobile>
                <SkeletonLine width="88px" height="16px" $mb={8} />
              </HideOnMobile>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexDirection: "column",
                }}
              >
                <FlexContainer>
                  <div style={{ flex: "1 1 0", maxWidth: "60%" }}>
                    <SkeletonLine height="16px" />
                  </div>
                  <SkeletonLine width="20%" height="16px" />
                </FlexContainer>

                <ShowOnMobile>
                  <FlexContainer>
                    {" "}
                    <div style={{ flex: "1 1 0", maxWidth: "60%" }}>
                      <SkeletonLine height="16px" />
                    </div>
                    <SkeletonLine width="20%" height="16px" />
                  </FlexContainer>
                </ShowOnMobile>
              </div>
            </FlexColumn>
          </RightCard>
        </RightColumn>

        <BottomContainer>
          <SkeletonLine height="32px" width="126px" />
          <SkeletonLine width="150px" height="40px" />
        </BottomContainer>
      </PageLayout>
    </SkeletonWrapper>
  </>
);

export default SplitOrderSkeleton;
